<template>
  <div id="plugins">
    <!-- Plugins -->
    <b-card
      id="blockPluginsCard"
      :class="$store.state.drawer.plugins.position"
      class="blockDrawer"
    >
      <template v-slot:header>
        <a
          class="close clickable"
          @click="$store.commit('hideDrawer', 'plugins')"
        >
          <i class="far fa-times"></i>
        </a>
        <h4 class="card-title">Plugins</h4>
        <b-btn
          @click="display.modal.pluginLibrary = true"
          class="mb-2 mr-1"
          v-if="$store.state.role == 'Administrator'"
        >
          <i class="fas fa-book-open"></i> Plugin Library
        </b-btn>
      </template>

      <div class="list-group">
        <a
          @click="launchRasterAnalysis()"
          class="list-group-item list-group-item-action clickable"
        >
          <p class="mb-1"><i class="fas fa-file-image"></i> Raster Analysis<br><small>Extract data from raster datasets.</small></p>
        </a>
        <a
          @click="launchInterpolator()"
          class="list-group-item list-group-item-action clickable"
        >
          <p class="mb-1"><i class="fad fa-th"></i> Interpolator<br><small>Smooth your data using interpolation.</small></p>
        </a>
        <a
          @click="launchGridGenerator()"
          class="list-group-item list-group-item-action clickable"
        >
          <p class="mb-1"><i class="fas fa-border-none"></i> Grid Generator<br><small>Create grids and random points.</small></p>
        </a>
        <!-- Mission Dispatch — restricted: only farms granted 'missions'
             via the super-admin FarmManager toggle see this entry. It is
             deliberately absent from pluginsDirectory so the self-serve
             library can never activate it. -->
        <a
          v-if="farm.plugins && farm.plugins.includes('missions')"
          @click="launchMissions()"
          class="list-group-item list-group-item-action clickable"
        >
          <p class="mb-1"><i class="fas fa-robot"></i> Missions<br><small>Dispatch and monitor robot missions.</small></p>
        </a>
        <a
          @click="launchTranslator()"
          class="list-group-item list-group-item-action clickable"
        >
          <p class="mb-1"><i class="fas fa-sort-shapes-up"></i> Translator<br><small>Translate a dataset with sample points.</small></p>
        </a>
        <a
          @click="launchDataJoiner()"
          class="list-group-item list-group-item-action clickable"
        >
          <p class="mb-1"><i class="fas fa-folder-tree"></i> Data Joiner<br><small>Join several datasets together.</small></p>
        </a>
        <a
          @click="launchMultivariate()"
          class="list-group-item list-group-item-action clickable"
        >
          <p class="mb-1"><i class="fas fa-chart-scatter"></i> Multivariate Zoning<br><small>Create zones with many variables.</small></p>
        </a>
        <!-- <a
          v-if="showVariableRateMode"
          @click="launchVariableRateMode()"
          class="list-group-item list-group-item-action clickable"
        > -->
        <a
          @click="launchVariableRateMode()"
          class="list-group-item list-group-item-action clickable"
        >
          <p class="mb-1"><i class="fas fa-sliders-h"></i> Variable Rate Mode<br><small>Configure variable rate applications.</small></p>
        </a>
        <!--
          Satellite Imagery has graduated to a native panel — see the
          satellite icon in the top-right menu beside Collect Data. The
          legacy full-page route (/satellite-imagery) remains available
          as an advanced-search fallback for archival sources.
        -->
        <a
          @click="launchFigures()"
          class="list-group-item list-group-item-action clickable"
        >
          <p class="mb-1"><i class="fas fa-chart-line"></i> Figures Generator<br><small>Create beautiful, customizable figures from your datasets.</small></p>
        </a>
        <!-- Crop Picker — Every.Farm's public, cited catalog of alternative
             crops at crops.every.farm. Opens in a new tab for now; the
             longer-term tie-in is pre-filling its farm-conditions matcher
             from this farm's blocks (see ROADMAP Track B). -->
        <a
          :href="cropPickerUrl"
          target="_blank"
          rel="noopener"
          class="list-group-item list-group-item-action clickable"
        >
          <p class="mb-1">
            <i class="fas fa-seedling"></i> Crop Picker
            <span class="badge badge-pill badge-warning ml-1" style="font-size: 0.7em; vertical-align: middle;">Experimental</span>
            <i class="fas fa-external-link-alt float-right text-muted mt-1" style="font-size: 0.8em;"></i>
            <br><small>Screen alternative crops for your region — a cited catalog you can match to your farm's conditions. Opens crops.every.farm.</small>
          </p>
        </a>
        <!-- <a
          @click="launchPlugin('Crop Load Calculator', 'https://crop.efficientvineyard.com', '')"
          class="list-group-item list-group-item-action clickable"
        >
          <p class="mb-1"><i class="fas fa-calculator"></i> Crop Load Calculator<br><small>Predict future croploads.</small></p>
        </a> -->
      </div><!-- /.list-group -->

      <div
        v-for="plugin in pluginsDirectory"
        v-bind:key="plugin.id"
      >
        <div
          class="card mt-2"
          v-if="farm.plugins && farm.plugins.includes(plugin.id)"
        >
          <div class="card-body">
            <img
              :src="plugin.logo.url"
              class="card-img-top bg-secondary p-4"
              :alt="plugin.logo.alt"
            />
            <h5 class="card-title mt-2">{{ plugin.name }}</h5>
            <p class="card-text">{{ plugin.description }}</p>
            <button
              class="btn btn-success"
              @click="launchPlugin(plugin.name, plugin.url, $store.state.farm.id, plugin.authRequired,)"
            >Launch</button>
          </div>
        </div>
      </div>

    </b-card>

    <!-- PLUGIN LIBRARY MODAL -->
    <PluginLibrary
      v-if="display.modal.pluginLibrary"
    />
    <!-- PLUGIN MODAL -->
    <PluginModal
      v-if="display.modal.pluginModal"
    />
  </div><!-- /#blockPlugins -->
</template>

<script>
import { mapState } from 'vuex'
import PluginLibrary from './PluginLibrary'
import PluginModal from './PluginModal'

export default {
  data () {
    return {
      name: '',
      url: ''
    }
  },
  computed: {
    ...mapState([
      'display',
      'farm',
      'plugin',
      'pluginsDirectory'
    ]),
    showVariableRateMode () {
      return this.$route.query.mypad === '1'
    },
    // Tagged so Crop Picker analytics can tell app referrals from organic
    // traffic. No farm identifiers leave the app.
    cropPickerUrl () {
      return 'https://crops.every.farm/crops?utm_source=app.every.farm&utm_medium=plugins-drawer'
    }
  },
  components: {
    PluginLibrary,
    PluginModal
  },
  methods: {
    launchPlugin (name, url, arg1, authRequired) {
      this.plugin.name = name
      this.plugin.url = url
      this.plugin.authRequired = authRequired || false
      if (arg1) {
        this.plugin.arg1 = arg1
      }
      this.display.modal.pluginModal = true
    },
    launchVariableRateMode () {
      // Hide the plugins drawer
      this.$store.commit('hideDrawer', 'plugins')
      // Navigate directly to the Variable Rate Mode page
      this.$router.push('/variable-rate-mode')
    },
    launchSatelliteImagery () {
      // Hide the plugins drawer
      this.$store.commit('hideDrawer', 'plugins')
      // Navigate directly to the Satellite Imagery page
      this.$router.push('/satellite-imagery')
    },
    launchFigures () {
      // Hide the plugins drawer
      this.$store.commit('hideDrawer', 'plugins')
      // Navigate directly to the Figures page
      this.$router.push('/figures')
    },
    launchRasterAnalysis () {
      // Hide the plugins drawer
      this.$store.commit('hideDrawer', 'plugins')
      // Navigate directly to the in-codebase Raster Analysis page
      this.$router.push('/raster-analysis')
    },
    launchInterpolator () {
      // Hide the plugins drawer
      this.$store.commit('hideDrawer', 'plugins')
      // Navigate directly to the in-codebase Interpolator page
      this.$router.push('/interpolator')
    },
    launchGridGenerator () {
      // Hide the plugins drawer
      this.$store.commit('hideDrawer', 'plugins')
      // Navigate directly to the in-codebase Grid Generator page
      this.$router.push('/grid-generator')
    },
    launchMissions () {
      this.$store.commit('hideDrawer', 'plugins')
      this.$router.push('/missions')
    },
    launchTranslator () {
      // Hide the plugins drawer
      this.$store.commit('hideDrawer', 'plugins')
      // Navigate directly to the in-codebase Translator page
      this.$router.push('/translator')
    },
    launchDataJoiner () {
      // Hide the plugins drawer
      this.$store.commit('hideDrawer', 'plugins')
      // Navigate directly to the in-codebase Data Joiner page
      this.$router.push('/data-joiner')
    },
    launchMultivariate () {
      // Hide the plugins drawer
      this.$store.commit('hideDrawer', 'plugins')
      // Navigate directly to the in-codebase Multivariate Zoning page
      this.$router.push('/multivariate')
    }
  }
}
</script>
